/**
 * Feature Flag Merger
 * Merges flags from multiple sources based on priority
 */

import { FeatureFlag, FeatureFlagSource, FeatureFlagContext } from './types';
import { PRIORITY_LEVELS } from './constants';

export function mergeFlags(
  sources: FeatureFlagSource[]
): Record<string, FeatureFlag> {
  // Sort sources by priority (highest first)
  const sortedSources = sources.sort((a, b) => b.priority - a.priority);
  
  const merged: Record<string, FeatureFlag> = {};
  
  // Process from lowest to highest priority (reverse order)
  // This ensures higher priority flags overwrite lower ones
  sortedSources.reverse().forEach(source => {
    Object.entries(source.flags).forEach(([key, flag]) => {
      merged[key] = flag;
    });
  });
  
  return merged;
}

export function evaluateFlag(
  flag: FeatureFlag,
  context: FeatureFlagContext
): boolean {
  // Check if flag is enabled at all
  if (!flag.enabled) return false;
  
  // Check date constraints
  if (!isWithinDateRange(flag)) return false;
  
  // Check tenant constraints
  if (flag.tenantIds && flag.tenantIds.length > 0) {
    if (!context.tenantId || !flag.tenantIds.includes(context.tenantId)) {
      return false;
    }
  }
  
  // Check role constraints
  if (flag.userRoles && flag.userRoles.length > 0) {
    if (!context.userRole || !flag.userRoles.includes(context.userRole)) {
      return false;
    }
  }
  
  // Check user constraints
  if (flag.userIds && flag.userIds.length > 0) {
    if (!context.userId || !flag.userIds.includes(context.userId)) {
      return false;
    }
  }
  
  return true;
}

function isWithinDateRange(flag: FeatureFlag): boolean {
  const now = new Date();
  
  if (flag.startDate) {
    const start = new Date(flag.startDate);
    if (now < start) return false;
  }
  
  if (flag.endDate) {
    const end = new Date(flag.endDate);
    if (now > end) return false;
  }
  
  return true;
}
